import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Bot, Send } from "lucide-react";

const SUGGESTIONS = [
  "Quels sous-traitants sont conformes ?",
  "Combien de béton pour une dalle de 10×5×0,15 m ?",
  "Quels devis sont en attente ?",
  "Quel matériel est disponible ?",
];

export default function Assistant() {
  const { user } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      const sid = resp.headers.get("X-Session-Id");
      if (sid) setSessionId(sid);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const token = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
              return copy;
            });
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Erreur de connexion à l'assistant." }; return c; });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] max-w-3xl" data-testid="assistant-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-amber-500 flex items-center justify-center"><Bot className="h-6 w-6 text-slate-900" /></div>
        <div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">TECA Assistant</h1>
          <p className="text-xs text-muted-foreground">Réponses sourcées sur les données TECA — n'invente jamais prix, normes ni clients.</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 bg-card rounded-xl border border-border p-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Bonjour {user?.name?.split(" ")[0] || ""} — posez une question métier :</p>
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid={`suggestion-${s.slice(0, 20).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => send(s)}
                className="block w-full text-left text-sm font-semibold bg-muted hover:bg-amber-100 dark:hover:bg-amber-950 rounded-lg px-3 py-2.5 transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-teca-800 text-white ml-auto" : "bg-muted"}`}
            data-testid={`chat-msg-${i}`}>
            {m.content || (loading && i === messages.length - 1 ? "…" : "")}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-3">
        <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ex. : Combien coûterait ce chantier ?" className="py-3.5 text-base" disabled={loading} />
        <Button data-testid="chat-send" type="submit" disabled={loading || !input.trim()}
          className="h-13 w-13 bg-amber-500 text-slate-900 hover:bg-amber-400 touch-target">
          <Send className="h-5 w-5" />
        </Button>
      </form>
    </div>
  );
}
